import { TlfItemIndexReadDto } from "../tlfItem/TlfItemIndexReadDto";
import { TlfBundleExportRequest } from "./interface";

export interface BuildTlfBundleExportRequestParams {
  hierarchyId: string;
  items: TlfItemIndexReadDto[];
  selectedItemIds: string[];
  includeComments: boolean;
  includeDescendants: boolean;
  includeParentHierarchyArsMetadata: boolean;
  exportOnlyReferencedMetadata: boolean;
  ignoreRetiredAndDecommissioned: boolean;
}

/**
 * Build the smallest valid selection payload for an export.
 *
 * @param params - Hierarchy, full item set, and selected export options
 * @param params.hierarchyId - Identifier of the hierarchy being exported
 * @param params.items - TLF index rows available for export
 * @param params.selectedItemIds - Identifiers selected for the export
 * @param params.includeComments - Whether to include comments in the bundle
 * @param params.includeDescendants - Whether to include descendant report items
 * @param params.includeParentHierarchyArsMetadata - Whether to include parent hierarchy ARS metadata
 * @param params.exportOnlyReferencedMetadata - Whether to limit metadata to referenced entries
 * @param params.ignoreRetiredAndDecommissioned - Whether to omit retired and decommissioned items
 * @returns Export request using All, Only, or AllExcept with the smallest item list
 */
export const buildTlfBundleExportRequest = ({
  hierarchyId,
  items,
  selectedItemIds,
  includeComments,
  includeDescendants,
  includeParentHierarchyArsMetadata,
  exportOnlyReferencedMetadata,
  ignoreRetiredAndDecommissioned,
}: BuildTlfBundleExportRequestParams): TlfBundleExportRequest => {
  const selectedIds = new Set(selectedItemIds);
  const selected = items.filter((item) => selectedIds.has(item.id));
  const deselected = items.filter((item) => !selectedIds.has(item.id));

  if (selected.length === items.length) {
    return {
      hierarchyId,
      mode: "All",
      items: [],
      includeComments,
      includeDescendants,
      includeParentHierarchyArsMetadata,
      exportOnlyReferencedMetadata,
      ignoreRetiredAndDecommissioned,
    };
  }

  const useOnly = selected.length <= deselected.length;
  return {
    hierarchyId,
    mode: useOnly ? "Only" : "AllExcept",
    items: (useOnly ? selected : deselected).map((item) => item.id),
    includeComments,
    includeDescendants,
    includeParentHierarchyArsMetadata,
    exportOnlyReferencedMetadata,
    ignoreRetiredAndDecommissioned,
  };
};
